import { apiClient } from './client';

export { apiClient } from './client';
import { fetchOrganizations } from './endpoints/organizations';
import {
  createProject as createProjectRequest,
  fetchProject,
  fetchProjectsForOrganization,
} from './endpoints/projects';
import { fetchPlatformService, fetchPlatformServices } from './endpoints/platformServices';
import type { CreateProject } from './types/project';
import { ProfileSchema, type Profile, type UpdateProfile } from './types/profile';

function slugifyName(name: string) {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
}

export const projectsApi = {
  async getProjects() {
    const orgs = await fetchOrganizations();
    const out: Array<
      Awaited<ReturnType<typeof fetchProjectsForOrganization>>[number] & {
        organizationId: string;
        organizationName: string;
      }
    > = [];
    for (const o of orgs) {
      const items = await fetchProjectsForOrganization(o.id);
      for (const p of items) {
        out.push({ ...p, organizationId: o.id, organizationName: o.name });
      }
    }
    return out;
  },
  getProject: fetchProject,
  async createProject(data: CreateProject) {
    const orgId = data.organizationId ?? data.organization_id;
    if (!orgId) {
      throw new Error('organizationId is required');
    }
    const slug = (data.slug && data.slug.trim()) || slugifyName(data.name);
    return createProjectRequest({
      organizationId: orgId,
      name: data.name.trim(),
      slug,
      description: data.description?.trim() || undefined,
    });
  },
};

export const billingApi = {
  getSubscription: () => apiClient.get('billing/subscription').json(),
  getPayments: () => apiClient.get('billing/payments').json(),
  getResourceUsage: () => apiClient.get('billing/usage').json(),
};

export const servicesApi = {
  getServices: fetchPlatformServices,
  getService: fetchPlatformService,
};

export const profileApi = {
  getProfile: async (): Promise<Profile> => {
    const data = await apiClient.get('profile/me').json();
    return ProfileSchema.parse(data);
  },
  updateProfile: async (data: UpdateProfile): Promise<Profile> => {
    const res = await apiClient.patch('profile/me', { json: data }).json();
    return ProfileSchema.parse(res);
  },
};
